/**
 * Table Extractor
 *
 * Extracts structured table data from accessibility tree snapshots and formats
 * as CSV with metadata frontmatter. Supports pagination via append mode.
 *
 * @module browser-core/table-extractor
 */

import { parseSnapshot, extractSnapshotYaml, type SnapshotElement } from './snapshot-analyzer.js';
import { detectGrid } from './snapshot-formatter-grid.js';
import type { GridInfo } from './snapshot-formatter-shared.js';

// ==================== Types ====================

export interface TableExtractionOptions {
  /** Friendly name for the table (used in frontmatter) */
  name?: string;
  /** Source URL for frontmatter metadata */
  sourceUrl?: string;
  /** Ref of specific grid to extract. Omit to extract the largest. */
  ref?: string;
}

export interface TableExtractionResult {
  /** Column headers */
  columns: string[];
  /** Row data (each row is an array of cell values aligned to columns) */
  rows: string[][];
  /** Ref of the extracted grid element */
  gridRef: string;
  /** Pagination info if detected nearby */
  pagination?: { showing: string; total: number };
}

export interface CSVFormatOptions {
  name?: string;
  sourceUrl?: string;
  totalHint?: number;
  showingHint?: string;
}

// ==================== Core Functions ====================

/**
 * Parse a snapshot (YAML string or MCP response) and extract table data
 * from the largest or specified grid.
 */
export function extractTablesFromSnapshot(
  yamlOrResponse: string,
  options?: TableExtractionOptions
): TableExtractionResult | null {
  // If it looks like an MCP response, extract YAML first
  const yaml = yamlOrResponse.includes('Page Snapshot:')
    ? extractSnapshotYaml(yamlOrResponse)
    : yamlOrResponse;

  if (!yaml) return null;

  const elements = parseSnapshot(yaml);
  if (elements.length === 0) return null;

  // Find all grids in the tree
  const grids: { grid: GridInfo; element: SnapshotElement }[] = [];
  const findGrids = (els: SnapshotElement[]): void => {
    for (const el of els) {
      const grid = detectGrid(el);
      if (grid && grid.rows.length > 0) {
        grids.push({ grid, element: el });
      }
      // Don't recurse into grids — detectGrid already processes children
      if (!grid) {
        findGrids(el.children);
      }
    }
  };
  findGrids(elements);

  if (grids.length === 0) return null;

  // Select the target grid
  let target: { grid: GridInfo; element: SnapshotElement };
  if (options?.ref) {
    const match = grids.find((g) => g.grid.ref === options.ref);
    if (!match) return null;
    target = match;
  } else {
    // Pick the largest grid by row count
    target = grids.reduce((best, current) =>
      current.grid.rows.length > best.grid.rows.length ? current : best
    );
  }

  const { columns, rows } = gridInfoToRows(target.grid);
  if (columns.length === 0) return null;

  // Try to detect pagination info near the grid
  const pagination = detectPaginationInfo(elements, target.grid.ref);

  return {
    columns,
    rows,
    gridRef: target.grid.ref,
    pagination: pagination ?? undefined,
  };
}

/**
 * Convert a GridInfo structure into flat tabular data.
 * Uses positional column mapping — each cell is placed in its column by
 * matching cell.column against the header columns.
 */
export function gridInfoToRows(grid: GridInfo): { columns: string[]; rows: string[][] } {
  const columns = grid.columns.map((c: { name: string }) => c.name);
  if (columns.length === 0) return { columns: [], rows: [] };

  const columnIndex = new Map<string, number>();
  for (let i = 0; i < columns.length; i++) {
    columnIndex.set(columns[i], i);
  }

  const rows: string[][] = [];
  for (const row of grid.rows) {
    const values = Array.from<string>({ length: columns.length }).fill('');
    for (const cell of row.cells) {
      const idx = columnIndex.get(cell.column);
      if (idx !== undefined) {
        values[idx] = cleanCellValue(cell.value, cell.gridcellLabel);
      }
    }
    rows.push(values);
  }

  return { columns, rows };
}

/** SVG/icon artifacts that appear as cell values instead of actual data */
const SVG_ARTIFACT_PATTERN = /^Styled\(svg\)$/i;

/**
 * Clean a cell value for data extraction. When the grid cell picked up an
 * interactive element (e.g. a copy-button SVG) instead of the data text,
 * fall back to the gridcellLabel which contains the cell's own text.
 */
function cleanCellValue(value: string, gridcellLabel?: string): string {
  if (!value || SVG_ARTIFACT_PATTERN.test(value)) {
    // Fall back to gridcellLabel, stripping any SVG artifacts from it
    if (gridcellLabel) {
      return gridcellLabel.replace(/\s*Styled\(svg\)\s*/gi, '').trim();
    }
    return '';
  }
  return value;
}

/**
 * Format tabular data as a clean CSV (no frontmatter).
 * Metadata is conveyed in the tool result text, not in the file,
 * so `pd.read_csv(path)` works without any special options.
 */
export function formatCSVWithFrontmatter(
  columns: string[],
  rows: string[][],
  _options?: CSVFormatOptions
): string {
  const lines: string[] = [];

  // Header row
  lines.push(columns.map(escapeCSVField).join(','));

  // Data rows
  for (const row of rows) {
    lines.push(row.map(escapeCSVField).join(','));
  }

  return lines.join('\n') + '\n';
}

/**
 * Append new rows to an existing CSV string. Deduplicates by comparing
 * the string representation of each row.
 */
export function appendRowsToCSV(
  existingCSV: string,
  newRows: string[][],
  columns: string[],
  _options?: CSVFormatOptions
): string {
  // Parse existing rows (skip any legacy comment lines and header)
  const existingLines = existingCSV.split('\n').filter((l) => l.trim() !== '');
  const dataLines: string[] = [];
  let headerLine = '';

  for (const line of existingLines) {
    if (line.startsWith('#')) continue;
    if (!headerLine) {
      headerLine = line;
      continue;
    }
    dataLines.push(line);
  }

  // Build set of existing rows for dedup
  const existingRowSet = new Set(dataLines);

  // Add only new unique rows
  const newRowStrings = newRows.map((row) => row.map(escapeCSVField).join(','));
  const addedRows: string[] = [];
  for (const rowStr of newRowStrings) {
    if (!existingRowSet.has(rowStr)) {
      addedRows.push(rowStr);
      existingRowSet.add(rowStr);
    }
  }

  const allDataLines = [...dataLines, ...addedRows];
  const result = [headerLine || columns.map(escapeCSVField).join(','), ...allDataLines];
  return result.join('\n') + '\n';
}

/**
 * Search for pagination text (e.g. "1–50 of 9,020") near a grid element.
 */
export function detectPaginationInfo(
  elements: SnapshotElement[],
  _gridRef: string
): { showing: string; total: number } | null {
  // Walk all elements looking for pagination patterns
  const paginationPattern = /(\d+)\s*[-–]\s*(\d+)\s+of\s+([\d,]+)/i;

  const searchText = (els: SnapshotElement[]): { showing: string; total: number } | null => {
    for (const el of els) {
      const text = el.text || '';
      const match = text.match(paginationPattern);
      if (match) {
        const total = parseInt(match[3].replace(/,/g, ''), 10);
        return { showing: `${match[1]}-${match[2]}`, total };
      }
      const childResult = searchText(el.children);
      if (childResult) return childResult;
    }
    return null;
  };

  return searchText(elements);
}

// ==================== Helpers ====================

/**
 * Escape a field for CSV output. Wraps in quotes if it contains
 * commas, quotes, or newlines.
 */
function escapeCSVField(field: string): string {
  if (!field) return '';
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}
